import type { Partner } from "@expert/shared-types";

import { getFeature } from "@asurion-core/expert-workspace-feature-flagging";
import { sendEventMetric, withMetrics } from "@expert/cloudwatch-metrics";
import { getLogger } from "@expert/logging";
import { HTTPError } from "ky";

import type { Session } from "@/sdk/sessions/types";

import { env } from "@/config";
import { useAgentAttributesStore } from "@/sdk/agent/store/agent-attributes.store";
import { isExpertUniversitySesssion, isSimulatedVoiceTask, isVoiceTask } from "@/sdk/agent/voice";
import { userCache } from "@/shared-utils";

import type { C360Interaction, C360InteractionCreator } from "./api/interactions";

import { wrapInteractionLine, createInteraction, joinInteraction, toC360Summary } from "./api/interactions";
import { c360Dispatcher } from "./c360Analytics";
import { useC360InteractionStore } from "./store/useC360InteractionStore";

const logger = getLogger({ module: "c360" });
export const metricDimensions = (partner: Partner) => ({ partner, component: "ui-orchestration" });
const isJoinRefused = (error: unknown) => error instanceof HTTPError && error.response.status === 422;

/** Whose interaction our line is on: the IVR's (joined), our own (created), or none (the start failed). */
type RecordedLine =
    | { source: C360InteractionCreator; interaction: C360Interaction }
    | { source: "none"; interaction?: never };

// The start in flight for each session, so a session that ends first still has its line closed.
const pendingStarts = new Map<string, Promise<void>>();

/**
 * Records a voice call in C360, when recording is on for the partner: joins the interaction the IVR
 * opened if the call's contact attributes carry its ID, otherwise creates one. Never throws: recording
 * must not affect the call, so failures are only logged and counted.
 */
export async function startC360Interaction(session: Session, partner: Partner): Promise<void> {
    const task = session.currentTask;
    if (!isVoiceTask(task) || isSimulatedVoiceTask(task) || isExpertUniversitySesssion(session.id)) return;
    const ivrInteractionId = task.ivrInfo?.interactionId?.trim() || undefined;
    const pending = recordCall(session.id, partner, task.mdn, ivrInteractionId);
    pendingStarts.set(session.id, pending);
    await pending;
}

/** The session's C360 line, once any start still in flight has finished; undefined when it has none. */
export async function getC360Line(sessionId: string): Promise<C360Interaction | undefined> {
    await pendingStarts.get(sessionId);
    return useC360InteractionStore.getState().getInteraction(sessionId);
}

/** Closes the session's C360 line, if it has one, with the AI summary. Never throws. */
export async function endC360InteractionLine(session: Session, partner: Partner, summary?: string): Promise<void> {
    const sessionId = session.id;
    // Claimed before the first await, because `onSessionEnd` can run twice: the second call closes nothing.
    const pendingStart = pendingStarts.get(sessionId);
    pendingStarts.delete(sessionId);
    await pendingStart;
    const interaction = useC360InteractionStore.getState().getInteraction(sessionId);
    useC360InteractionStore.getState().removeSession(sessionId);
    // No line: the start didn't open one. No flag check here, so a line still closes if it's turned off mid-call.
    if (!interaction) return;
    const c360Summary = toC360Summary(summary).summary;
    logger.info(
        {
            sessionId,
            partner,
            interactionLineId: interaction.interactionLineId,
            c360CreatedBy: interaction.createdBy,
            c360SummarySent: !!c360Summary,
            c360SummaryLength: c360Summary?.length ?? 0,
        },
        "C360 wrapping interaction line",
    );
    await withMetrics(
        "C360WrapInteractionLine",
        () =>
            wrapInteractionLine({
                sessionId,
                partner,
                interactionLineId: interaction.interactionLineId,
                createdBy: interaction.createdBy,
                summary,
            }),
        {
            logger,
            rethrow: false,
            logResult: false,
            // `source`, like the start's: the collector drops a dimension it doesn't support, such as `createdBy`.
            dimensions: {
                ...metricDimensions(partner),
                ...(interaction.createdBy ? { source: interaction.createdBy } : {}),
            },
        },
    );
}

async function recordCall(
    sessionId: string,
    partner: Partner,
    mdn: string,
    ivrInteractionId: string | undefined,
): Promise<void> {
    try {
        if (!(await isC360FlagOn("isC360InteractionRecordingEnabled", partner))) {
            logger.debug({ partner }, "C360 recording off for partner");
            void c360Dispatcher(sessionId, partner).dispatchBusinessEvent("C360InteractionRecording", {
                recordingEnabled: false,
            });
            return;
        }
        const outcome = ivrInteractionId ? "ivr_id_found" : "ivr_id_not_found";
        const { interaction, source } = ivrInteractionId
            ? await joinOrCreate(sessionId, partner, mdn, ivrInteractionId)
            : await create(sessionId, partner, mdn);
        if (interaction) {
            useC360InteractionStore.getState().setInteraction(sessionId, { ...interaction, createdBy: source });
        }
        void sendEventMetric("C360InteractionStart", { ...metricDimensions(partner), outcome, source });
        void c360Dispatcher(sessionId, partner).dispatchBusinessEvent("C360InteractionRecording", {
            recordingEnabled: true,
            source,
            ivrInteractionFound: !!ivrInteractionId,
        });
        logger.info(
            {
                sessionId,
                partner,
                c360Outcome: outcome,
                c360Source: source,
                interactionId: interaction?.interactionId,
                interactionLineId: interaction?.interactionLineId,
            },
            "C360 interaction started",
        );
    } catch (error) {
        logger.warn({ error, sessionId, partner }, "C360 interaction recording could not start");
    }
}

/** Joins the IVR's interaction, or creates one on a 422 refusal; after other failures the line may exist already. */
async function joinOrCreate(
    sessionId: string,
    partner: Partner,
    mdn: string,
    interactionId: string,
): Promise<RecordedLine> {
    try {
        const interaction = await withMetrics(
            "C360JoinInteraction",
            () => joinInteraction({ sessionId, partner, interactionId }),
            // A refused join is expected and falls back to a create, so it isn't counted as a failure.
            { logger, logResult: false, dimensions: metricDimensions(partner), shouldCountAsCancelled: isJoinRefused },
        );
        return interaction ? { interaction, source: "ivr" } : { source: "none" };
    } catch (error) {
        if (!isJoinRefused(error)) return { source: "none" };
        logger.warn({ sessionId, interactionId }, "C360 rejected joining the IVR interaction; creating one");
        return create(sessionId, partner, mdn);
    }
}

async function create(sessionId: string, partner: Partner, mdn: string): Promise<RecordedLine> {
    const interaction = await withMetrics(
        "C360CreateInteraction",
        () => createInteraction({ sessionId, partner, mdn }),
        {
            logger,
            rethrow: false,
            logResult: false,
            dimensions: metricDimensions(partner),
        },
    );
    return interaction ? { interaction, source: "exwo" } : { source: "none" };
}

/**
 * A C360 feature flag for the partner's calls; off when it can't be read, since `getFeature` then returns undefined.
 * Read with the same context as `useFeatureFlag`, so the history panel and the history request can't disagree.
 */
export async function isC360FlagOn(
    flag: "isC360InteractionRecordingEnabled" | "isC360CustomerHistoryEnabled",
    partner: Partner,
): Promise<boolean> {
    const enabled = await getFeature<boolean>(flag, {
        appName: "expert-workspace",
        employeeId: userCache.employeeId ?? userCache.identity,
        environment: env.mode,
        channel: env.channel,
        agentSite: useAgentAttributesStore.getState().site,
        partner,
        source: "expert-ui",
    });
    return enabled === true;
}
